import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, MapPin, Phone, Mail, Clock } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { CONTACT, SERVICE_SLUGS, BOOKING_DEPOSIT } from "../constants/site";
import Reveal from "../components/Reveal";
import Field from "../components/Field";
import LeafletMap from "../components/LeafletMap";
import { useSeo } from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLanguage();
  useSeo(
    "Contact & Book a Ride | MIRAL GROUP",
    "Book your premium Tesla ride in Antwerp — 24/24 concierge, airport meet & greet at BRU and CRL. Contact MIRAL GROUP: contact@miralgroup.net."
  );
  const [values, setValues] = useState({
    full_name: "", email: "", phone: "", service_type: "airport",
    pickup_address: "", destination_address: "", pickup_datetime: "", notes: ""
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [paying, setPaying] = useState(false);

  const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (!values.full_name || values.full_name.trim().length < 2) e.full_name = t.errors.required;
    if (!values.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = t.errors.email;
    if (!values.phone || values.phone.replace(/\D/g, "").length < 8) e.phone = t.errors.phone;
    if (!values.pickup_address || values.pickup_address.trim().length < 4) e.pickup_address = t.errors.required;
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    try {
      const payload = { ...values, lang };
      ["destination_address", "pickup_datetime", "notes"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      const res = await axios.post(`${API}/contacts`, payload);
      toast.success(t.contact.form.successTitle);
      setSubmitted(res.data.id);
      setValues({
        full_name: "", email: "", phone: "", service_type: "airport",
        pickup_address: "", destination_address: "", pickup_datetime: "", notes: ""
      });
    } catch (err) {
      toast.error(t.errors.generic);
    } finally {
      setBusy(false);
    }
  };

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: BOOKING_DEPOSIT.lookupKey,
        origin_url: window.location.origin,
        contact_ref: submitted
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(t.errors.generic);
      setPaying(false);
    }
  };

  const markers = [
    { position: CONTACT.hq, title: t.contact.markerHqTitle, subtitle: t.contact.markerHqSub },
    { position: CONTACT.airport, title: t.contact.markerAirportTitle, subtitle: t.contact.markerAirportSub }
  ];

  return (
    <main className="pt-[72px]">
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <p className="eyebrow">{t.contact.eyebrow}</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight">
              {t.contact.title}
            </h1>
            <p className="mt-6 max-w-[60ch] text-lg text-slate-300">{t.contact.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 grid gap-8 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div data-testid="contact-info-card" className="flex h-full flex-col gap-6 rounded-2xl border border-white/10 bg-[#0F1015] p-7 lg:p-9">
              <h2 className="font-display text-xl font-bold text-white">{t.contact.infoTitle}</h2>

              <div className="flex items-start gap-4">
                <MapPin size={19} className="mt-0.5 shrink-0 text-gold" />
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-slate-500">{t.contact.office}</p>
                  <p className="mt-1 text-sm text-slate-200">{CONTACT.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone size={19} className="mt-0.5 shrink-0 text-gold" />
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-slate-500">{t.contact.phoneLabel}</p>
                  <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="mt-1 block text-sm text-slate-200 hover:text-gold-soft transition-colors">
                    {CONTACT.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail size={19} className="mt-0.5 shrink-0 text-gold" />
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-slate-500">{t.contact.emailLabel}</p>
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="mt-1 block text-sm text-slate-200 hover:text-gold-soft transition-colors">
                    {CONTACT.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock size={19} className="mt-0.5 shrink-0 text-gold" />
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-slate-500">{t.contact.dispatchLabel}</p>
                  <p className="mt-1 text-sm font-mono2 text-gold-soft tracking-[0.08em]">{t.contact.dispatchValue}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <div data-testid="contact-form" className="rounded-2xl border border-white/10 bg-[#0F1015] p-7 lg:p-9">
              <h2 className="font-display text-xl lg:text-2xl font-bold text-white">{t.contact.formTitle}</h2>

              {submitted ? (
                <div data-testid="contact-form-success" className="mt-8 rounded-xl border border-gold/30 bg-gold/5 p-6 text-center">
                  <CheckCircle2 size={36} className="mx-auto text-gold" />
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{t.contact.form.successTitle}</h3>
                  <p className="mt-2 text-sm text-slate-300">{t.contact.form.successBody}</p>
                  <p className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    {t.contact.form.ref}: <span className="text-gold-soft">{submitted}</span>
                  </p>
                  <div data-testid="booking-deposit-box" className="mt-6 rounded-xl border border-gold/25 bg-gold/5 p-5 text-left">
                    <h4 className="font-display text-base font-bold text-white">{t.pay.depositTitle}</h4>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{t.pay.depositBody}</p>
                    <button
                      type="button"
                      data-testid="pay-deposit-button"
                      disabled={paying}
                      onClick={payDeposit}
                      className="btn-gold mt-4 w-full text-xs font-mono2 uppercase tracking-[0.18em] disabled:opacity-60"
                    >
                      {paying ? t.pay.paying : t.pay.depositBtn}
                    </button>
                  </div>
                  <button
                    type="button"
                    data-testid="contact-form-again-button"
                    onClick={() => setSubmitted(null)}
                    className="btn-outline mt-6 !py-2.5 !px-6 text-[11px] font-mono2 uppercase tracking-[0.18em]"
                  >
                    {t.contact.form.again}
                  </button>
                </div>
              ) : (
                <form className="mt-7 grid gap-5 sm:grid-cols-2" data-testid="contact-form-fields" onSubmit={onSubmit} noValidate>
                  <Field id="contact-name" label={t.contact.form.name} error={errors.full_name} required>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      className={`field-input ${errors.full_name ? "has-error" : ""}`}
                      value={values.full_name}
                      onChange={set("full_name")}
                      autoComplete="name"
                    />
                  </Field>
                  <Field id="contact-email" label={t.contact.form.email} error={errors.email} required>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      className={`field-input ${errors.email ? "has-error" : ""}`}
                      value={values.email}
                      onChange={set("email")}
                      autoComplete="email"
                    />
                  </Field>
                  <Field id="contact-phone" label={t.contact.form.phone} error={errors.phone} required>
                    <input
                      id="contact-phone"
                      data-testid="contact-phone-input"
                      type="tel"
                      className={`field-input ${errors.phone ? "has-error" : ""}`}
                      value={values.phone}
                      onChange={set("phone")}
                      autoComplete="tel"
                    />
                  </Field>
                  <Field id="contact-service" label={t.contact.form.service} required>
                    <select
                      id="contact-service"
                      data-testid="contact-service-select"
                      className="field-input"
                      value={values.service_type}
                      onChange={set("service_type")}
                    >
                      {SERVICE_SLUGS.map((slug) => (
                        <option key={slug} value={slug}>{t.contact.serviceOptions[slug]}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="contact-pickup" label={t.contact.form.pickup} error={errors.pickup_address} required>
                    <input
                      id="contact-pickup"
                      data-testid="contact-pickup-input"
                      className={`field-input ${errors.pickup_address ? "has-error" : ""}`}
                      value={values.pickup_address}
                      onChange={set("pickup_address")}
                      placeholder={t.contact.form.pickupPh}
                    />
                  </Field>
                  <Field id="contact-dest" label={t.contact.form.dest}>
                    <input
                      id="contact-dest"
                      data-testid="contact-destination-input"
                      className="field-input"
                      value={values.destination_address}
                      onChange={set("destination_address")}
                      placeholder={t.contact.form.destPh}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field id="contact-datetime" label={t.contact.form.datetime}>
                      <input
                        id="contact-datetime"
                        data-testid="contact-datetime-input"
                        type="datetime-local"
                        className="field-input [color-scheme:dark]"
                        value={values.pickup_datetime}
                        onChange={set("pickup_datetime")}
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field id="contact-notes" label={t.contact.form.notes}>
                      <textarea
                        id="contact-notes"
                        data-testid="contact-notes-input"
                        rows={3}
                        className="field-input resize-none"
                        value={values.notes}
                        onChange={set("notes")}
                        placeholder={t.contact.form.notesPh}
                      />
                    </Field>
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={busy}
                    className="btn-gold sm:col-span-2 w-full text-xs font-mono2 uppercase tracking-[0.18em] disabled:opacity-60"
                  >
                    {busy ? "…" : t.contact.form.submit}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#090a0e] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <p className="eyebrow">{t.contact.mapTitle}</p>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">{t.contact.mapSub}</h2>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10">
              <LeafletMap center={CONTACT.hq} zoom={12} height={typeof window !== "undefined" && window.innerWidth < 768 ? "300px" : "440px"} markers={markers} />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
